import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { tenantApi, Integration } from '../lib/tenant-api';
import Modal from './Modal';
import { FileText, Download, Database } from 'lucide-react';
import WordPressImportModal from './WordPressImportModal';
import { FaDropbox, FaDrupal, FaGoogleDrive, FaHubspot, FaSalesforce, FaShopify, FaTypo3, FaWebflow, FaWordpress } from "react-icons/fa6";
import { CgMonday } from "react-icons/cg";
import { PiMicrosoftOutlookLogoFill } from "react-icons/pi";
import { GrOnedrive } from "react-icons/gr";
import { SiBigcommerce, SiBox, SiCalendly, SiGooglecalendar, SiWoocommerce, SiZoho } from "react-icons/si";
interface SourceSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentType: 'products' | 'services' | 'documents';
  onManualUpload: () => void;
}

const SourceSelectionModal: React.FC<SourceSelectionModalProps> = ({
  isOpen,
  onClose,
  contentType,
  onManualUpload,
}) => {
  const navigate = useNavigate();
  const [isWordPressImportOpen, setIsWordPressImportOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);

  const { data: integrations = [] } = useQuery<Integration[]>({
    queryKey: ['integrations'],
    queryFn: tenantApi.getIntegrations,
    enabled: isOpen,
  });

  const enabledIntegrations = integrations.filter(
    int => int.is_enabled
  );

  const handleManualUpload = () => {
    onClose();
    onManualUpload();
  };

  const handleCMSImport = (integration: Integration) => {
    setSelectedIntegration(integration);
    setIsWordPressImportOpen(true);
  };

  const getContentTypeLabel = () => {
    switch (contentType) {
      case 'products':
        return 'Products';
      case 'services':
        return 'Services';
      case 'documents':
        return 'Documents';
      default:
        return 'Content';
    }
  };

  const getContentTypeDescription = () => {
    switch (contentType) {
      case 'products':
        return 'Choose how you want to add products to your catalog';
      case 'services':
        return 'Choose how you want to add services to your catalog';
      case 'documents':
        return 'Choose how you want to add documents to your knowledge base';
      default:
        return 'Choose how you want to add content';
    }
  };


  const switchIntegrationIcon = (integrationType: string) => {
    switch (integrationType) {
      case 'wordpress':
        return <FaWordpress className="h-6 w-6 text-black" />;
      case 'webflow':
        return <FaWebflow className="h-6 w-6 text-indigo-600" />;
      case 'shopify':
        return <FaShopify className="h-6 w-6 text-green-500" />;
      case 'hubspot':
        return <FaHubspot className="h-6 w-6 text-orange-500" />;
      case 'salesforce':
        return <FaSalesforce className="h-6 w-6 text-indigo-500" />;
      case 'google_drive':
        return <FaGoogleDrive className="h-6 w-6 text-red-500" />;
      case 'dropbox':
        return <FaDropbox className="h-6 w-6 text-indigo-600" />;
      case 'outlook':
        return <PiMicrosoftOutlookLogoFill className="h-6 w-6 text-indigo-500" />;
      case 'one_drive':
        return <GrOnedrive className="h-6 w-6 text-indigo-600" />;
      case 'monday':
        return <CgMonday className="h-6 w-6 text-indigo-600" />;
      case 'zoho':
        return <SiZoho className="h-6 w-6 text-indigo-600" />;
      case 'box':
        return <SiBox className="h-6 w-6 text-indigo-600" />;
      case 'calendly':
        return <SiCalendly className="h-6 w-6 text-indigo-600" />;
      case 'typo3':
        return <FaTypo3 className="h-6 w-6 text-orange-500" />;
      case 'drupal':
        return <FaDrupal className="h-6 w-6 text-indigo-600" />;
      case 'google_calendar':
        return <SiGooglecalendar className="h-6 w-6 text-red-500" />;
      case 'bigcommerce':
        return <SiBigcommerce className="h-6 w-6 text-indigo-600" />;
      case 'woocommerce':
        return <SiWoocommerce className="h-6 w-6 text-purple-500" />;
      default:
        return <Database className="h-6 w-6 text-indigo-600" />;
    }
  }

  return (
    <>
      <Modal
        isOpen={isOpen && !isWordPressImportOpen}
        onClose={onClose}
        title={`Add ${getContentTypeLabel()}`}
      >
        <div className="space-y-6">
          <p className="text-sm text-gray-600">
            {getContentTypeDescription()}
          </p>

          <div className="grid grid-cols-1 gap-4">
            {/* Manual Upload Option */}
            <button
              onClick={handleManualUpload}
              className="flex items-center p-4 border-2 border-gray-200  hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-left"
            >
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-indigo-100  flex items-center justify-center">
                  <FileText className="h-6 w-6 text-indigo-600" />
                </div>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-gray-900">Manual Upload</h3>
                <p className="text-sm text-gray-500">
                  Upload {contentType} from a Markdown file
                </p>
              </div>
              <div className="flex-shrink-0">
                <Download className="h-5 w-5 text-gray-400" />
              </div>
            </button>

            {/* CMS Import Options */}
            {enabledIntegrations.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="flex-1 border-t border-gray-200"></div>
                  <div className="px-3 text-sm text-gray-500">or import from CMS</div>
                  <div className="flex-1 border-t border-gray-200"></div>
                </div>

                {enabledIntegrations.map((integration) => (
                  <button
                    key={integration.id}
                    onClick={() => handleCMSImport(integration)}
                    className="flex items-center p-4 border-2 border-gray-200  hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-left w-full"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gray-100  flex items-center justify-center">
                        {switchIntegrationIcon(integration.integration_type)}
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <h3 className="text-lg font-medium text-gray-900">
                        Import from {integration.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Import {contentType} from your {integration.integration_type} connection
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <Download className="h-5 w-5 text-gray-400" />
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* No integrations message */}
            {enabledIntegrations.length === 0 && (
              <div className="text-center py-6 border-2 border-dashed border-gray-200 ">
                <Database className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No CMS integrations</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Set up CMS integrations in Settings to import content automatically.
                </p>
                <div className="mt-3">
                  <button
                    onClick={() => {
                      onClose();
                      navigate('/settings?tab=integrations');
                    }}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium  text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Go to Settings
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300  hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
        </div>
      </Modal>

      {/* WordPress Import Modal */}
      {selectedIntegration && (
        <WordPressImportModal
          isOpen={isWordPressImportOpen}
          onClose={() => {
            setIsWordPressImportOpen(false);
            setSelectedIntegration(null);
            onClose();
          }}
          integration={selectedIntegration}
          contentType={contentType}
        />
      )}
    </>
  );
};

export default SourceSelectionModal;
